import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { UrlEnum } from '@enums/url.enum';
import { LocalStorageService } from '@shared/services/local-storage/local-storage.service';
import { UrlService } from '@shared/services/url/url.service';
import { Observable, throwError } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { RouteParamMapEnum } from '@enums/route-param-map.enum';
import { Order, OrderListItem, SearchOrdersPayload } from './_interfaces_/order.interface';
import {
  TicketConditionsResponse,
  BookedOrderTicketConditionsResponse,
  TicketConditionsMapped,
  TicketConditionsTitle,
  TicketConditionsTitleMapped,
} from './_interfaces_/ticket-conditions.interface';
import { OrderCancellationResponse } from '../../shared/services/option-order/_interfaces_/option-order-details.mapped.interface';
import { SendDocumentResponse } from './_interfaces_/send-document-response.interface';

@Injectable({
  providedIn: 'root',
})
export class CommonOrderService {
  constructor(
    @Inject(HttpClient) private httpClient: HttpClient,
    @Inject(UrlService) private urlService: UrlService,
    @Inject(LocalStorageService) private localStorageService: LocalStorageService
  ) {}

  public getOptionOrders(): Observable<OrderListItem[]> {
    const headers = { Sessionid: this.localStorageService.getUserSessionToken() };
    const culture = this.localStorageService.getCulture();
    const url = new URL(`${this.urlService.urls[UrlEnum.GET_OPTION_ORDERS]}`);
    url.searchParams.append('culture', culture);

    return this.httpClient.get<OrderListItem[]>(url.toString(), { headers });
  }

  public searchOrders(params: SearchOrdersPayload): Observable<OrderListItem[]> {
    const headers = { Sessionid: this.localStorageService.getUserSessionToken() };
    const culture = this.localStorageService.getCulture();
    const url = new URL(`${this.urlService.urls[UrlEnum.GET_ORDERS]}`);
    url.searchParams.append('culture', culture);

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.append(key, value);
      }
    });

    return this.httpClient.get<OrderListItem[]>(url.toString(), { headers });
  }

  public getOrder(orderReference: string, culture: string) {
    const url: string = `${this.urlService.urls[UrlEnum.GET_COMMON_ORDER_DETAILS]}?${RouteParamMapEnum.OrderReference}=${orderReference}&${
      RouteParamMapEnum.Culture
    }=${culture}`;
    const headers = { Sessionid: this.localStorageService.getUserSessionToken() };
    return this.httpClient.get(url, { headers }).pipe(
      take(1),
      map((response) => {
        return response;
      }),
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  public getActionInfo(orderReference: string, pnr: string, culture: string): Observable<Order> {
    const url: string = `${this.urlService.urls[UrlEnum.GET_COMMON_ORDER_DETAILS_WITH_ACTION_INFO]}?${
      RouteParamMapEnum.OrderReference
    }=${orderReference}&${RouteParamMapEnum.PNR}=${pnr}&${RouteParamMapEnum.Culture}=${culture}`;
    const headers = { Sessionid: this.localStorageService.getUserSessionToken() };
    return this.httpClient.get<Order>(url, { headers }).pipe(
      take(1),
      map((response) => {
        return response;
      }),
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  public toPascalCase(s: string | null | undefined) {
    return s ? s.replace(/(\w)(\w*)/g, (_, p, q) => p.toUpperCase() + q.toLowerCase()) : s;
  }

  public getTicketConditions(flightIdentifier: string): Observable<TicketConditionsResponse> {
    const culture = this.localStorageService.getCulture();
    const headers = { SessionId: this.localStorageService.getUserSessionToken() };
    const url: string = `${this.urlService.urls[UrlEnum.GET_TICKET_CONDITIONS]}/${flightIdentifier}?culture=${culture}`;

    return this.httpClient.get<TicketConditionsResponse>(url, { headers }).pipe(
      take(1),
      map((response) => {
        return response;
      })
    );
  }

  public mapTicketConditions(ticketConditions: BookedOrderTicketConditionsResponse): TicketConditionsMapped {
    const { ErrorMessage: error } = ticketConditions;
    const { Headers: headers } = ticketConditions?.DossierDetails;
    return {
      error,
      conditions: headers
        ? headers.map((headerObj) => ({
            id: headerObj.Header.toLocaleLowerCase().replace(/\s/g, ''),
            header: headerObj.Header,
            titles: this.mapTicketTitles(headerObj.Titles),
          }))
        : [],
    };
  }

  public mapTicketTitles(titles: TicketConditionsTitle[]): TicketConditionsTitleMapped[] {
    return titles.map((title) => ({
      title: title.Title,
      texts: title.TextLines.map((text) => text.Text),
    }));
  }

  public postInvoiceRequest(dossierNumber: string) {
    const url = `${this.urlService.urls[UrlEnum.SEND_INVOICE]}?dossierNumber=${dossierNumber}`;
    const headers = new HttpHeaders({
      Sessionid: this.localStorageService.getUserSessionToken(),
    });

    return this.httpClient.post<SendDocumentResponse>(url, {}, { headers });
  }

  public postETicketRequest(dossierNumber: string) {
    const url = `${this.urlService.urls[UrlEnum.SEND_ETICKET]}?dossierNumber=${dossierNumber}`;
    const headers = new HttpHeaders({
      Sessionid: this.localStorageService.getUserSessionToken(),
    });

    return this.httpClient.post<SendDocumentResponse>(url, {}, { headers });
  }

  public deleteOrder(orderIdentifier: string): Observable<OrderCancellationResponse> {
    const url: string = `${this.urlService.urls[UrlEnum.DELETE_COMMON_ORDER]}?orderIdentifier=${orderIdentifier}`;
    const sessionId: string = this.localStorageService.getUserSessionToken();
    const headers = { SessionId: sessionId };

    return this.httpClient.delete<OrderCancellationResponse>(url, { headers });
  }

  public validateOptionOrder(orderReference: string): Observable<any> {
    const culture = this.localStorageService.getCulture();
    const url: string = `${this.urlService.urls[UrlEnum.VALIDATE_OPTION_ORDER]}?orderReference=${orderReference}&culture=${culture}`;
    const sessionId: string = this.localStorageService.getUserSessionToken();
    const headers = { SessionId: sessionId };

    return this.httpClient.put(url, {}, { headers });
  }

  public finalizeOptionOrder(orderReference: string, orderIdentifier: string): Observable<any> {
    const culture = this.localStorageService.getCulture();

    const url: string = `${
      this.urlService.urls[UrlEnum.FINALIZE_OPTION_ORDER]
    }?orderReference=${orderReference}&orderIdentifier=${orderIdentifier}&culture=${culture}`;
    const sessionId: string = this.localStorageService.getUserSessionToken();
    const headers = { SessionId: sessionId };

    return this.httpClient.put(url, {}, { headers });
  }
}
